import { IconButton, InputAdornment, TextField } from '@mui/material'
import tw from 'twin.macro'
import { CircleCancelMinor, SearchMinor } from '@shopify/polaris-icons'
import React, { ChangeEvent, memo, useCallback, useEffect, useRef, useState } from 'react'
import _ from 'lodash'

interface Props {
  searchQuery: string
  onChangeSearchQuery: (search: string) => void
  className?: string
  width?: number
  inputDataTestId?: string
  clearButtonDataTestId?: string
}

const TableSearchComponent = ({
  searchQuery,
  onChangeSearchQuery,
  className,
  width = 400,
  inputDataTestId,
  clearButtonDataTestId,
}: Props) => {
  const searchDebounce = useRef(_.debounce(onChangeSearchQuery, 700))

  const [searchValue, setSearchValue] = useState<string>(searchQuery)

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value)
  }, [])

  useEffect(() => {
    searchDebounce.current(searchValue)
  }, [searchValue])

  const handleClear = useCallback(() => {
    setSearchValue('')
    onChangeSearchQuery('')
  }, [])

  return (
    <div
      css={[tw`bg-white flex rounded-lg flex-row overflow-hidden`, { width }]}
      className={className}
    >
      <TextField
        placeholder="Search"
        type="text"
        variant="outlined"
        fullWidth
        value={searchValue}
        size="small"
        onChange={handleSearchChange}
        inputProps={{
          'data-testid': inputDataTestId,
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchMinor width={20} />
            </InputAdornment>
          ),
          endAdornment: searchQuery && (
            <InputAdornment position="end">
              <IconButton
                data-testid={clearButtonDataTestId}
                css={tw`mr-[-12px] rounded-none`}
                onClick={handleClear}
              >
                <CircleCancelMinor width={20} fill="#B4AFC0" />
              </IconButton>
            </InputAdornment>
          ),
          sx: {
            '&:not(.Mui-focused) .MuiOutlinedInput-notchedOutline': {
              border: '1px solid var(--Shades-Grey-100, #EDEDED)',
            },
          },
        }}
      />
    </div>
  )
}

export const TableSearch = memo(TableSearchComponent)
